import { log } from "@lululemon/mwa-utils";
import { taggedPromoTile } from "./tagged-promotile-util";

/**
 * getProductsWithPromoTilesAtInsertedPoint - insert promotiles at the generated insertpoint
 * @param {Array} productList - product list products data.
 * @param {Number} insertPoint - index to insert the promo tile.
 * @param {Array} promoTileData - data for grid item.
 */
export const getProductsWithPromoTilesAtInsertedPoint = (
  productList,
  insertPoint,
  promoTileData
) => {
  const insertedProductList = [...productList];

  // Insert promo tile if it doesn't already exist
  if (
    productList[insertPoint]?.fields?.rowOrder !==
    promoTileData?.fields?.rowOrder
  ) {
    if (insertPoint <= productList.length) {
      insertedProductList.splice(insertPoint, 0, promoTileData);
    }
    return insertedProductList;
  }
  return insertedProductList;
};

/**
 * getInsertPointAndOffset - get the point at which the promo tile should be inserted into the product list
 * @param {Array} gridItem - promo tile data array.
 * @param {String} bp - relates to the resolution of the screen.
 * @param {Number} promoTileOffset - offsets the product list based on promo tile insert point
 */
export const getInsertPointAndOffset = (gridItem = {}, bp, promoTileOffset) => {
  let offset = promoTileOffset;
  const { rowOrder, layout, placement } = gridItem?.fields || {};
  const isMobile = bp === "small" || bp === "mobile";
  const colCount = isMobile ? 2 : 3;
  // Set default insert point based on length of products and alignment - default is 'left'
  let insertPoint = (rowOrder - 1) * colCount;
  // offset for each double promo tile above this one since they are two column elements
  insertPoint -= offset;

  if (!isMobile) {
    if (layout === "singleTile") {
      if (placement === "right") insertPoint += 2; // move tile 2 columns over
      if (placement === "center") insertPoint += 1; // move tile 1 column over
    } else {
      // double tile && backwards compatibility
      if (placement === "right") insertPoint += 1; // move tile 1 column over
      // increment the offset for subsequent promo tiles
      offset += 1;
    }
  } else {
    // tiles are full row width on Mobile, so just
    // get the correct row by offsetting for each promo tile above
    offset += 1;
  }

  return { offset, insertPoint };
};

/**
 * getProductAndPromoList - Get new products list along with promo tiles.
 * @param {Object} props - properties.
 * @param {Array} props.products - original product list array .
 * @param {string} props.bp - breakpoint of the screen.
 * @param {Array} props.promo - list of available promo tile products.
 */
export const getProductAndPromoList = ({ products, bp, promo }) => {
  let newProductList = [...products];

  if (promo) {
    let promoTileOffset = 0;

    // need to sort the array since we are inserting objects at specific locations
    const sortedPromoArray = promo.sort(
      (a, b) =>
        parseInt(a?.fields?.rowOrder, 10) - parseInt(b?.fields?.rowOrder, 10)
    );

    // eslint-disable-next-line no-unused-expressions
    sortedPromoArray?.forEach((gridItem) => {
      const { offset, insertPoint } = getInsertPointAndOffset(
        gridItem,
        bp,
        promoTileOffset
      );
      promoTileOffset = offset;

      // Insert promotional tile if it doesn't already exist
      newProductList = getProductsWithPromoTilesAtInsertedPoint(
        newProductList,
        insertPoint,
        gridItem
      );
    });
  }

  return newProductList;
};

/**
 * Contentful Grid items may now have 2 Promo Tile instances in its "components" field
 * This function determines which of the configured Promo Tiles we should use.
 */
export const getPromoTileToShowFromContentfulGridItem = ({
  contentfulGridItem,
  isUserLoggedIn = false,
}) => {
  if (!contentfulGridItem) return;

  const contentfulPromoTileOptions =
    contentfulGridItem?.fields?.components || [];
  const notLoggedInPromoTile = contentfulPromoTileOptions?.[0];
  const matchingTaggedPromoTile = taggedPromoTile(
    contentfulPromoTileOptions,
    isUserLoggedIn
  );

  const contentfulPromoData = matchingTaggedPromoTile
    ? matchingTaggedPromoTile
    : notLoggedInPromoTile;

  if (contentfulPromoData?.fields?.desktopMedia) {
    contentfulPromoData.fields.imageUrl = undefined;
  }
  return contentfulPromoData;
};

export const getBraQuizResultPillLabel = (productData = {}, braQuizResults) => {
  const availableSizes = productData["all-available-sizes"];

  if (!braQuizResults || !Object.keys(braQuizResults).length || !availableSizes)
    return null;

  const { letterSizeLabel, numberedSizeLabel, coverages, ...resultSizes } =
    braQuizResults;
  const resultLabels = {
    bandSize: resultSizes.bandSize,
    letteredSize: letterSizeLabel,
    numberedSize: numberedSizeLabel,
  };
  const intendedCupSize = productData["intended-cup-size"] || [];

  const nonNullResults = (key) => !!braQuizResults[key];
  const normalizeSize = (size) => size.replace(/\s/g, "");
  const matchedResultKey = (key) =>
    availableSizes.some(
      (size) => normalizeSize(resultSizes[key]) === normalizeSize(size)
    );

  const matchedKey = Object.keys(resultSizes)
    .filter(nonNullResults)
    .find(matchedResultKey);
  const pillLabel = resultLabels[matchedKey] || null;

  if (!pillLabel && matchedKey && matchedKey !== "americanSize") {
    log.warning("Unknown key matched for bra quiz results pill label:", {
      matchedKey,
    });
  }

  if (matchedKey === "numberedSize") {
    return intendedCupSize.some((cup) => coverages?.includes(cup))
      ? pillLabel
      : null;
  }

  return pillLabel;
};

/**
 * Normalize a contentful object into a quick dictionary for grabbing product details by productId
 * Accepts: `Web > Component > CDP Product Details List` content model
 *
 * @param {Object} productDetails - Contentful object List of Product Details for items in the CDP
 *
 * @returns {Map<String, Object>} - Map of the product details by productId.
 *
 * @example
 *    Input: {
 *      contentfulEntryName: "Shoe's CDP",
 *      productDetailsRef: [
 *        {
 *            fields: { contentfulEntryName: "Blissfeel Shoe", productId: "prod123456", productDetails: "", productDetailsAlternate: "" },
 *            metadata: { ... },
 *            sys: { ... },
 *        }
 *      ]
 *    }
 *    Return: {
 *        "prod123456": {
 *            contentfulEntryName: "Blissfeel Shoe", productId: "prod123456", productDetails: "", productDetailsAlternate: ""
 *        }
 *    }
 */
export const getNormalizedProductDetailsList = (productDetails) => {
  if (!productDetails?.productDetailsRef) {
    log.debug("Invalid productDetails object provided to normalize function");
    return new Map();
  }
  return productDetails?.productDetailsRef?.reduce((productMap, curr) => {
    if (!curr.fields) return productMap;
    const { productId } = curr?.fields;
    productMap.set(productId, { ...curr?.fields });
    return productMap;
  }, new Map());
};
